import { useMarketingViewSection, useMarketingViewRestoring } from '../components/MarketingViewsProvider';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LazyReportSection } from '../hooks/useLazySection';
import { Download } from 'lucide-react';
import { bingDashboardApi, type BingDateGrain, type BingReportParams, type BingReportResponse, type BingReportRow, type BingReportType } from '../api/bingDashboard';
import { ColumnToggle } from '../components/ColumnToggle';
import { alignMarketingDateRange, getCurrentCalendarRange, DELIVERY_RANGE_PRESETS, MarketingTimeRangeFilter } from '../components/MarketingTimeRangeFilter';
import { MultiSelect } from '../components/MultiSelect';
import { ReportLabelSelect } from '../components/ReportLabelSelect';
import { TableLoadingCell, TableLoadingOverlay, TableLoadingState } from '../components/TableLoadingOverlay';
import { sf, type FieldKey } from '../lib/fieldPolicy';
import { useAppStore } from '../store/useAppStore';
import { useDownloadCenterStore } from '../store/useDownloadCenterStore';
import { useToastStore } from '../store/useToastStore';

type DatePreset = 'today' | 'yesterday' | '7d' | '14d' | '30d' | 'thisWeek' | 'thisMonth' | 'custom';
type DetailDimension = 'date' | 'new_old_type' | 'ad_form_type' | 'campaign_id' | 'campaign_name';

interface MetricField {
  key: string;
  label: string;
  fmt: (value: number) => string;
  permKey?: FieldKey;
}

interface SectionDef {
  report: Exclude<BingReportType, 'overview'>;
  title: string;
  dimensions: DetailDimension[];
  filterField?: 'ad_form_type' | 'new_old_type';
}

const DATE_GRAINS: Array<{ value: BingDateGrain; label: string }> = [
  { value: 'day', label: '日' },
  { value: 'week', label: '周' },
  { value: 'month', label: '月' },
  { value: 'year', label: '年' },
];

const DIMENSIONS: Array<{ key: DetailDimension; label: string }> = [
  { key: 'date', label: '日期维度' },
  { key: 'new_old_type', label: '广告新老客' },
  { key: 'ad_form_type', label: '广告形式' },
  { key: 'campaign_id', label: '广告系列id' },
  { key: 'campaign_name', label: '广告系列名称' },
];

const UNSUPPORTED_DIMENSIONS_BY_REGION: Partial<Record<string, readonly DetailDimension[]>> = {
  ca: ['campaign_id'],
  uk: ['campaign_id'],
  de: ['campaign_id'],
};

const SECTIONS: SectionDef[] = [
  { report: 'ad_form', title: '广告形式报表', dimensions: ['ad_form_type', 'date'], filterField: 'ad_form_type' },
  { report: 'new_old', title: '新老客报表', dimensions: ['new_old_type', 'date'], filterField: 'new_old_type' },
  { report: 'detail', title: '明细报表', dimensions: ['date'] },
];

const BING_LABEL_FIELDS = ['new_old_type', 'ad_form_type'] as const;

const BING_DIMENSION_COLUMN_WIDTH: Record<DetailDimension, number> = {
  date: 140,
  new_old_type: 160,
  ad_form_type: 160,
  campaign_id: 160,
  campaign_name: 260,
};

function bingDimensionLeft(dimensions: DetailDimension[], index: number): number {
  return dimensions.slice(0, index).reduce((total, key) => total + BING_DIMENSION_COLUMN_WIDTH[key], 0);
}

const fmtNum = (value: number, digits = 2) => Number(value ?? 0).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
const fmtInt = (value: number) => Math.round(Number(value ?? 0)).toLocaleString('en-US');
const fmtPct = (value: number) => `${(Number(value ?? 0) * 100).toFixed(2)}%`;

const METRICS: MetricField[] = [
  { key: 'spend', label: '账户花费', fmt: fmtNum, permKey: 'spend' },
  { key: 'conversions_value', label: '购物转化价值', fmt: fmtNum, permKey: 'revenue' },
  { key: 'impressions', label: '展示次数', fmt: fmtInt },
  { key: 'clicks', label: '点击次数', fmt: fmtInt },
  { key: 'add_to_cart', label: '加购次数', fmt: fmtNum },
  { key: 'conversions', label: '购物次数', fmt: fmtNum },
  { key: 'account_roi', label: '账户ROI', fmt: fmtNum, permKey: 'roas' },
  { key: 'cpa', label: '账户CPA', fmt: fmtNum, permKey: 'cpa' },
  { key: 'ctr', label: '账户CTR', fmt: fmtPct },
  { key: 'cpc', label: '账户CPC', fmt: fmtNum, permKey: 'cpc' },
  { key: 'click_add_to_cart_rate', label: '点击加购率', fmt: fmtPct },
  { key: 'click_purchase_rate', label: '点击购买率', fmt: fmtPct },
  { key: 'cost_per_add_to_cart', label: '单次加购费用', fmt: fmtNum, permKey: 'cpa' },
  { key: 'aov', label: '账户单均价', fmt: fmtNum, permKey: 'revenue' },
];

const daysAgo = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
};

const today = () => new Date().toISOString().slice(0, 10);

const getPresetRange = (preset: DatePreset, start: string, end: string) => {
  if (preset === 'custom') return { start, end };
  if (preset === 'thisWeek' || preset === 'thisMonth') return getCurrentCalendarRange(preset, today());
  if (preset === 'today') return { start: today(), end: today() };
  if (preset === 'yesterday') return { start: daysAgo(1), end: daysAgo(1) };
  if (preset === '14d') return { start: daysAgo(13), end: today() };
  if (preset === '30d') return { start: daysAgo(29), end: today() };
  return { start: daysAgo(6), end: today() };
};

const isZeeloolSite = (brand: string, siteCode: string) => brand === 'zeelool' && /^(us|ca|uk|de)(-|$)/i.test(siteCode);
const dimensionLabel = (key: string) => DIMENSIONS.find(item => item.key === key)?.label ?? key;

function cleanParams(params: BingReportParams): BingReportParams {
  return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== '' && value != null)) as BingReportParams;
}

function BingReportSection({ section, baseParams, userLevel, unsupportedDimensionKeys = [] }: { section: SectionDef; baseParams: Omit<BingReportParams, 'report' | 'dimensions'> | null; userLevel: number; unsupportedDimensionKeys?: readonly DetailDimension[] }) {
  const isRestoringView = useMarketingViewRestoring();
  const [dimensions, setDimensions] = useState<DetailDimension[]>(section.dimensions);
  const [filterValues, setFilterValues] = useState<string[]>([]);
  const [rows, setRows] = useState<BingReportRow[]>([]);
  const [summary, setSummary] = useState<BingReportRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hiddenMetricKeys, setHiddenMetricKeys] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(`bing_dashboard_hidden_cols_${section.report}`);
      if (raw) return new Set(JSON.parse(raw) as string[]);
    } catch {}
    return new Set();
  });
  const enqueueReportExport = useDownloadCenterStore(state => state.enqueueReportExport);
  const showToast = useToastStore(state => state.show);
  const activeDimensions = section.report === 'detail' ? dimensions : section.dimensions;
  const availableDimensions = useMemo(
    () => DIMENSIONS.filter(item => !unsupportedDimensionKeys.includes(item.key)),
    [unsupportedDimensionKeys],
  );
  const availableMetrics = useMemo(
    () => METRICS,
    [],
  );
  const visibleMetrics = useMemo(() => availableMetrics.filter(metric => !hiddenMetricKeys.has(metric.key)), [availableMetrics, hiddenMetricKeys]);
  const metricToggleCols = useMemo(() => availableMetrics.map(metric => ({ key: metric.key, label: metric.label })), [availableMetrics]);

  const params = useMemo(() => {
    if (!baseParams) return null;
    const filter = section.filterField && filterValues.length > 0 ? { [section.filterField]: filterValues.join(',') } : {};
    return cleanParams({ ...baseParams, ...filter, report: section.report, dimensions: activeDimensions.join(',') });
  }, [activeDimensions, baseParams, filterValues, section.filterField, section.report]);

  useMarketingViewSection('detail:' + section.report, {
    label: section.title, state: { dimensions, filterValues, hiddenMetricKeys: [...hiddenMetricKeys] },
    restore: value => { setDimensions(value.dimensions); setFilterValues(value.filterValues); setHiddenMetricKeys(new Set(value.hiddenMetricKeys)); },
    loading: loading, afterQuery: () => { void fetchData(); },
  });

  const fetchData = useCallback(async () => {
    if (!params || isRestoringView()) return;
    setLoading(true);
    setError('');
    try {
      const response = await bingDashboardApi.getReport(params);
      const data: BingReportResponse = response.data;
      setRows(data?.list ?? []);
      setSummary(data?.summary ?? null);
    } catch (cause) {
      setRows([]);
      setSummary(null);
      setError(cause instanceof Error ? cause.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [params, isRestoringView]);

  useEffect(() => { void fetchData(); }, [fetchData]);

  const toggleMetric = (key: string) => {
    setHiddenMetricKeys(current => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem(`bing_dashboard_hidden_cols_${section.report}`, JSON.stringify([...next])); } catch {}
      return next;
    });
  };

  const resetMetrics = () => {
    setHiddenMetricKeys(new Set());
    try { localStorage.removeItem(`bing_dashboard_hidden_cols_${section.report}`); } catch {}
  };

  const renderMetric = (row: BingReportRow | null, metric: MetricField) => {
    const raw = metric.fmt(row?.metrics?.[metric.key] ?? 0);
    return metric.permKey ? sf(raw, metric.permKey, userLevel).value ?? '锁定' : raw;
  };

  const exportReport = async () => {
    if (!params) return;
    try {
      await enqueueReportExport({
        report_key: 'ad_dashboard_bing_report',
        title: `Bing投放看板-${section.title}`,
        file_name: `Bing投放看板_${section.title}_${params.start_date}_${params.end_date}.csv`,
        brand_id: params.brand_id,
        site_id: params.site_id,
        params: params as unknown as Record<string, unknown>,
        columns: [
          ...activeDimensions.map(key => ({ key, label: dimensionLabel(key) })),
          ...visibleMetrics.map(metric => ({ key: metric.key, label: metric.label })),
        ],
      });
      showToast('导出任务已提交，请到右上角下载中心查看', 'success');
    } catch (cause) {
      showToast(cause instanceof Error ? cause.message : '导出任务提交失败', 'error');
    }
  };

  return <section className="card" style={{ marginTop: 16, padding: 0, overflow: 'hidden' }}>
    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border2)', display: 'flex', alignItems: 'center', gap: 12 }}>
      <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{section.title}</span>
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
        {loading && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text3)', fontSize: 12 }}>
          <i aria-hidden="true" style={{ width: 13, height: 13, borderRadius: '50%', border: '2px solid var(--border2)', borderTopColor: 'var(--primary)', animation: 'spin .8s linear infinite' }} />
          查询中…
        </span>}
        <button type="button" className="btn btn-outline btn-sm" onClick={() => void exportReport()} disabled={loading || !params} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Download size={14} />导出</button>
        <ColumnToggle cols={metricToggleCols} hiddenKeys={hiddenMetricKeys} onToggle={toggleMetric} onReset={resetMetrics} />
      </div>
    </div>

    {(section.filterField || section.report === 'detail') && <div style={{ padding: '12px 16px', display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 12, borderBottom: '1px solid var(--border2)' }}>
      {section.filterField && <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 180 }}>
        <span style={{ color: 'var(--text3)', fontSize: 11 }}>{section.filterField === 'ad_form_type' ? '广告形式' : '新老客类型'}</span>
        <ReportLabelSelect dataset="bing_delivery" field={section.filterField} pageFields={BING_LABEL_FIELDS} value={filterValues} onChange={setFilterValues} width={220} />
      </label>}
      {section.report === 'detail' && <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ color: 'var(--text3)', fontSize: 11, whiteSpace: 'nowrap' }}>动态维度</span>
        <MultiSelect options={availableDimensions.map(item => ({ value: item.key, label: item.label }))} value={availableDimensions.filter(item => activeDimensions.includes(item.key)).map(item => item.key)} onChange={value => setDimensions(value.length > 0 ? value as DetailDimension[] : ['date'])} placeholder="请选择维度" tagDisplay style={{ width: 440 }} />
      </div>}
    </div>}

    {error && <div style={{ margin: '12px 16px 0', color: 'var(--red)', fontSize: 13 }}>查询失败：{error}</div>}
    <div className="table-wrap" style={{ position: 'relative', maxHeight: section.report === 'detail' ? 620 : 420, overflow: 'auto' }}>
      <table style={{ minWidth: Math.max(1280, activeDimensions.reduce((total, key) => total + BING_DIMENSION_COLUMN_WIDTH[key], 0) + visibleMetrics.length * 96) }}>
        <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}><tr>
          {activeDimensions.map((key, index) => <th key={key} style={{
            position: 'sticky',
            left: bingDimensionLeft(activeDimensions, index),
            zIndex: 5,
            width: BING_DIMENSION_COLUMN_WIDTH[key],
            minWidth: BING_DIMENSION_COLUMN_WIDTH[key],
            maxWidth: BING_DIMENSION_COLUMN_WIDTH[key],
            background: 'var(--surface2)',
            boxShadow: index === activeDimensions.length - 1 ? '2px 0 4px rgba(15,23,42,.06)' : undefined,
          }}>{dimensionLabel(key)}</th>)}
          {visibleMetrics.map(metric => <th key={metric.key} style={{ background: 'var(--surface2)', textAlign: 'right', whiteSpace: 'nowrap' }}>{metric.label}</th>)}
        </tr></thead>
        <tbody>
          {summary && <tr style={{ fontWeight: 700, background: 'var(--surface)' }}>
            {activeDimensions.map((key, index) => <td key={key} style={{
              position: 'sticky',
              left: bingDimensionLeft(activeDimensions, index),
              zIndex: 2,
              width: BING_DIMENSION_COLUMN_WIDTH[key],
              minWidth: BING_DIMENSION_COLUMN_WIDTH[key],
              maxWidth: BING_DIMENSION_COLUMN_WIDTH[key],
              background: 'var(--surface)',
              boxShadow: index === activeDimensions.length - 1 ? '2px 0 4px rgba(15,23,42,.06)' : undefined,
            }}>{index === 0 ? '总计' : ''}</td>)}
            {visibleMetrics.map(metric => <td key={metric.key} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{renderMetric(summary, metric)}</td>)}
          </tr>}
          {loading && rows.length === 0 ? <tr><td colSpan={99}><TableLoadingCell /></td></tr>
            : !baseParams ? <tr><td colSpan={99} style={{ padding: 42, textAlign: 'center', color: 'var(--text3)' }}>请切换到 Zeelool US 站点后查看 Bing 报表。</td></tr>
              : rows.length === 0 ? <tr><td colSpan={99} style={{ padding: 42, textAlign: 'center', color: 'var(--text3)' }}>当前筛选条件下暂无数据，请调整时间范围或筛选项。</td></tr>
                : rows.map((row, index) => <tr key={`${activeDimensions.map(key => row.dimensions?.[key] ?? '').join('|')}-${index}`}>
                  {activeDimensions.map((key, dimensionIndex) => <td key={key} style={{
                    position: 'sticky',
                    left: bingDimensionLeft(activeDimensions, dimensionIndex),
                    zIndex: 1,
                    width: BING_DIMENSION_COLUMN_WIDTH[key],
                    minWidth: BING_DIMENSION_COLUMN_WIDTH[key],
                    maxWidth: BING_DIMENSION_COLUMN_WIDTH[key],
                    background: index % 2 === 0 ? 'var(--surface2)' : 'var(--surface)',
                    boxShadow: dimensionIndex === activeDimensions.length - 1 ? '2px 0 4px rgba(15,23,42,.06)' : undefined,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }} title={row.dimensions?.[key] ?? ''}>{row.dimensions?.[key] || '未标记'}</td>)}
                  {visibleMetrics.map(metric => <td key={metric.key} style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{renderMetric(row, metric)}</td>)}
                </tr>)}
        </tbody>
      </table>
      {loading && rows.length > 0 && <TableLoadingOverlay />}
    </div>
  </section>;
}

// Embedded below the legacy Bing overview so the original summary remains the
// first report and every report shares its selected date range/granularity.
export function ZeeloolBingReportSections({
  brandId,
  siteId,
  startDate,
  endDate,
  dateGrain,
  userLevel,
  region,
}: {
  brandId: string;
  siteId: string;
  startDate: string;
  endDate: string;
  dateGrain: BingDateGrain;
  userLevel: number;
  region?: string;
}) {
  const baseParams = useMemo(() => {
    if (!brandId || !siteId) return null;
    return { brand_id: brandId, site_id: siteId, start_date: startDate, end_date: endDate, date_grain: dateGrain };
  }, [brandId, dateGrain, endDate, siteId, startDate]);
  if (!baseParams) return null;
  return <>{SECTIONS.map(section => (
    <LazyReportSection key={section.report} enabled={Boolean(baseParams)}>
      <BingReportSection section={section} baseParams={baseParams} userLevel={userLevel} unsupportedDimensionKeys={UNSUPPORTED_DIMENSIONS_BY_REGION[region ?? ''] ?? []} />
    </LazyReportSection>
  ))}</>;
}

export default function ZeeloolBingReportDashboard() {
  const { brand, brandId, siteId, sites, sitesLoaded, adContext } = useAppStore();
  const [dateGrain, setDateGrain] = useState<BingDateGrain>('day');
  const [datePreset, setDatePreset] = useState<DatePreset>('7d');
  const [customStart, setCustomStart] = useState(() => daysAgo(6));
  const [customEnd, setCustomEnd] = useState(() => today());
  const [appliedRange, setAppliedRange] = useState(() => ({ start: daysAgo(6), end: today(), grain: 'day' as BingDateGrain }));
  const site = useMemo(() => sites.find(item => item.id === siteId), [siteId, sites]);
  const region = String(site?.code ?? '').split('-')[0]?.toLowerCase() ?? '';
  const supported = Boolean(site && isZeeloolSite(brand, site.code));
  const range = useMemo(() => alignMarketingDateRange(getPresetRange(datePreset, customStart, customEnd), dateGrain), [customEnd, customStart, dateGrain, datePreset]);
  const baseParams = useMemo(() => !brandId || !site || !supported ? null : {
    brand_id: brandId,
    site_id: site.id,
    start_date: appliedRange.start,
    end_date: appliedRange.end,
    date_grain: appliedRange.grain,
  }, [appliedRange, brandId, site, supported]);

  useEffect(() => {
    const timer = window.setTimeout(() => setAppliedRange({ start: range.start, end: range.end, grain: dateGrain }), 300);
    return () => window.clearTimeout(timer);
  }, [dateGrain, range.end, range.start]);

  if (!sitesLoaded) return <div className="page-section visible"><div className="card" style={{ padding: 0 }}><TableLoadingState /></div></div>;

  return <div className="page-section visible">
    <div className="dashboard-sticky-filters"><div className="data-filter-card"><div className="data-filter-row time">
      <MarketingTimeRangeFilter
        grain={dateGrain}
        grains={DATE_GRAINS}
        onGrainChange={setDateGrain}
        preset={datePreset}
        presets={DELIVERY_RANGE_PRESETS}
        onPresetChange={setDatePreset}
        start={customStart}
        end={customEnd}
        max={today()}
        customPreset="custom"
        onRangeChange={({ start, end }) => { setCustomStart(start); setCustomEnd(end); }}
      />
    </div></div></div>
    {!supported ? <div className="card" style={{ marginTop: 16, padding: 32, textAlign: 'center', color: 'var(--text2)' }}>广告形式、新老客及明细报表仅支持 Zeelool 的 US、CA、UK、DE Bing 数据。</div>
      : SECTIONS.map(section => (
        <LazyReportSection key={section.report} enabled={Boolean(baseParams)}>
          <BingReportSection section={section} baseParams={baseParams} userLevel={adContext?.data_level ?? 1} unsupportedDimensionKeys={UNSUPPORTED_DIMENSIONS_BY_REGION[region] ?? []} />
        </LazyReportSection>
      ))}
  </div>;
}
